import { useState, useEffect, useCallback } from "react";
import { Download01, TrendUp01, TrendDown01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { DatePicker } from "@/components/ui/date-picker";
import { StyledSelect } from "@/components/ui/styled-select";
import { getRevenueSummary, downloadReportCsv, type RevenueSummaryData } from "@/api/reports";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(amount);

function getDefaultDates() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];
  const to = now.toISOString().split("T")[0];
  return { from, to };
}

export default function RevenueSummaryTab() {
  const defaults = getDefaultDates();
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [groupBy, setGroupBy] = useState("day");
  const [data, setData] = useState<RevenueSummaryData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getRevenueSummary(from, to, groupBy);
      setData(result);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, groupBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    downloadReportCsv("revenue-summary", { from, to, groupBy }, `revenue-${from}-to-${to}.csv`);
  };

  const groupByOptions = [
    { label: "Daily", value: "day" },
    { label: "Weekly", value: "week" },
    { label: "Monthly", value: "month" },
  ];

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <DatePicker label="From" value={from} onChange={setFrom} />
        <DatePicker label="To" value={to} onChange={setTo} />
        <StyledSelect
          options={groupByOptions}
          value={groupBy}
          onChange={setGroupBy}
        />
        <Button size="md" color="secondary" iconLeading={Download01} onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-text-tertiary">Loading report...</div>
      ) : !data ? (
        <div className="py-12 text-center text-text-tertiary">Failed to load report</div>
      ) : (
        <>
          {/* Summary + Comparison */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <div className="rounded-xl border border-border-secondary bg-bg-primary p-4">
              <p className="text-xs text-text-tertiary mb-1">Total Revenue</p>
              <p className="text-xl font-semibold text-text-primary">{formatCurrency(data.totals.revenue)}</p>
              <p className="text-xs text-text-tertiary mt-0.5">{data.totals.invoiceCount} invoices</p>
            </div>
            <div className="rounded-xl border border-border-secondary bg-bg-primary p-4">
              <p className="text-xs text-text-tertiary mb-1">Previous Period</p>
              <p className="text-xl font-semibold text-text-primary">{formatCurrency(data.comparison.previousPeriodRevenue)}</p>
            </div>
            <div className="rounded-xl border border-border-secondary bg-bg-primary p-4">
              <p className="text-xs text-text-tertiary mb-1">Change</p>
              <div className="flex items-center gap-2">
                {data.comparison.changePercent >= 0 ? (
                  <TrendUp01 className="size-5 text-green-600" />
                ) : (
                  <TrendDown01 className="size-5 text-red-600" />
                )}
                <p className={`text-xl font-semibold ${data.comparison.changePercent >= 0 ? "text-green-600" : "text-red-600"}`}>
                  {data.comparison.changePercent > 0 ? "+" : ""}{data.comparison.changePercent}%
                </p>
              </div>
            </div>
          </div>

          {/* Bar Chart */}
          {data.periods.length > 0 ? (
            <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 mb-6">
              <h3 className="text-sm font-medium text-text-primary mb-4">Revenue Over Time</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.periods}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-secondary, #e5e7eb)" />
                    <XAxis
                      dataKey="periodLabel"
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => `€${v}`}
                    />
                    <Tooltip
                      formatter={(value) => [formatCurrency(Number(value)), "Revenue"]}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid var(--color-border-secondary, #e5e7eb)",
                        fontSize: "13px",
                      }}
                    />
                    <Bar dataKey="revenue" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-border-secondary bg-bg-primary p-8 text-center text-text-tertiary mb-6">
              No revenue data for this period
            </div>
          )}

          {/* Period Table */}
          {data.periods.length > 0 && (
            <div className="rounded-xl border border-border-secondary bg-bg-primary overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-secondary bg-bg-secondary">
                    <th className="px-6 py-3 text-left font-medium text-text-tertiary">Period</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary">Revenue</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary">Invoices</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary hidden sm:table-cell">Cash</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary hidden sm:table-cell">Card</th>
                    <th className="px-6 py-3 text-right font-medium text-text-tertiary hidden sm:table-cell">Bank</th>
                  </tr>
                </thead>
                <tbody>
                  {data.periods.map((p) => (
                    <tr key={p.period} className="border-b border-border-secondary last:border-0">
                      <td className="px-6 py-4 text-text-primary">{p.periodLabel}</td>
                      <td className="px-6 py-4 text-right font-medium text-text-primary">{formatCurrency(p.revenue)}</td>
                      <td className="px-6 py-4 text-right text-text-secondary">{p.invoiceCount}</td>
                      <td className="px-6 py-4 text-right text-text-secondary hidden sm:table-cell">{formatCurrency(p.byPaymentMethod.cash)}</td>
                      <td className="px-6 py-4 text-right text-text-secondary hidden sm:table-cell">{formatCurrency(p.byPaymentMethod.card)}</td>
                      <td className="px-6 py-4 text-right text-text-secondary hidden sm:table-cell">{formatCurrency(p.byPaymentMethod.bank_transfer)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-bg-secondary font-medium">
                    <td className="px-6 py-3 text-text-primary">Total</td>
                    <td className="px-6 py-3 text-right text-text-primary">{formatCurrency(data.totals.revenue)}</td>
                    <td className="px-6 py-3 text-right text-text-secondary">{data.totals.invoiceCount}</td>
                    <td colSpan={3} className="hidden sm:table-cell" />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
